import React from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    pageSize?: number;
    totalItems?: number;
    additionalActions?: React.ReactNode;
}

const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    pageSize = 10,
    totalItems = 0,
    additionalActions,
}) => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 4) {
                pages.push(1, 2, 3, 4, 5, '...', totalPages);
            } else if (currentPage >= totalPages - 3) {
                pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
            }
        }
        return pages;
    };

    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    return (
        <Box display="flex" alignItems="center" gap={2} bgcolor="var(--color-page-background)" width="100%" justifyContent="space-between" boxSizing="border-box">
            <Typography color="text.secondary" fontSize={13}>
                Showing {startItem}-{endItem} from {totalItems}
            </Typography>
            <Box display="flex" gap={1}>
                <IconButton
                    onClick={() => onPageChange(1)}
                    disabled={currentPage === 1}
                    aria-label="First page"
                    size="small"
                    sx={{
                        bgcolor: '#B4FFF4',
                        borderRadius: 2,
                        width: 32,
                        height: 32,
                        p: 0,
                        color: 'var(--color-primary)',
                    }}
                >
                    <FirstPageIcon />
                </IconButton>
                <IconButton
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                    size="small"
                    sx={{
                        bgcolor: '#B4FFF4',
                        borderRadius: 2,
                        width: 32,
                        height: 32,
                        p: 0,
                        color: 'var(--color-primary)',
                    }}
                >
                    <NavigateBeforeIcon />
                </IconButton>
                {getPageNumbers().map((page, idx) =>
                    page === '...' ? (
                        <Box
                            key={`ellipsis-${idx}`}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            width={32}
                            height={32}
                            borderRadius={2}
                            bgcolor="#B4FFF4"
                            color="var(--color-primary)"
                        >
                            ...
                        </Box>
                    ) : (
                        <Button
                            key={`page-${page}`}
                            onClick={() => onPageChange(Number(page))}
                            variant={typeof page === 'number' && currentPage === page ? 'contained' : 'outlined'}
                            color={typeof page === 'number' && currentPage === page ? 'primary' : 'inherit'}
                            size="small"
                            sx={{
                                minWidth: 32,
                                width: 32,
                                height: 32,
                                borderRadius: 2,
                                border: 'none',
                                boxShadow: 'none',
                                fontSize: 14,
                                color: typeof page === 'number' && currentPage === page ? '#fff' : 'var(--color-primary)',
                                fontWeight: typeof page === 'number' && currentPage === page ? 700 : 400,
                                bgcolor: typeof page === 'number' && currentPage === page ? '#00C2B3' : '#B4FFF4',
                                p: 0,
                            }}
                            aria-current={typeof page === 'number' && currentPage === page ? 'page' : undefined}
                        >
                            {page}
                        </Button>
                    )
                )}
                <IconButton
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                    size="small"
                    sx={{
                        bgcolor: '#B4FFF4',
                        borderRadius: 2,
                        width: 32,
                        height: 32,
                        p: 0,
                        color: 'var(--color-primary)',
                    }}
                >
                    <NavigateNextIcon />
                </IconButton>
                <IconButton
                    onClick={() => onPageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    aria-label="Last page"
                    size="small"
                    sx={{
                        bgcolor: '#B4FFF4',
                        borderRadius: 2,
                        width: 32,
                        height: 32,
                        p: 0,
                        color: 'var(--color-primary)',
                    }}
                >
                    <LastPageIcon />
                </IconButton>
            </Box>
            {additionalActions && <Box ml="auto">{additionalActions}</Box>}
        </Box>
    );
};

export default Pagination;